import React from 'react';
import {
  Box,
  Text,
  HStack,
  VStack,
  Badge,
  Icon,
  useColorModeValue,
  Button,
} from '@chakra-ui/react';
import { FileText, Download, FileSpreadsheet, File } from 'lucide-react';
import { Documento, DocumentType } from '../../types/chat';

interface DocumentCardProps {
  documento: Documento;
}

const getDocumentIcon = (tipo: DocumentType) => {
  switch (tipo) {
    case 'xlsx':
    case 'planilha':
      return FileSpreadsheet;
    case 'txt':
      return File;
    case 'pdf':
    case 'relatorio':
    case 'nota_fiscal':
    case 'balancete':
    default:
      return FileText;
  }
};

const getDocumentColor = (tipo: DocumentType) => {
  switch (tipo) {
    case 'pdf':
      return 'red';
    case 'xlsx':
    case 'planilha':
      return 'green';
    case 'txt':
      return 'gray';
    case 'nota_fiscal':
      return 'blue';
    case 'balancete':
      return 'purple';
    case 'relatorio':
      return 'orange';
    default:
      return 'gray';
  }
};

const getDocumentLabel = (tipo: DocumentType) => {
  switch (tipo) {
    case 'pdf':
      return 'PDF';
    case 'xlsx':
      return 'Excel';
    case 'txt':
      return 'Texto';
    case 'planilha':
      return 'Planilha';
    case 'nota_fiscal':
      return 'Nota Fiscal';
    case 'balancete':
      return 'Balancete';
    case 'relatorio':
      return 'Relatório';
    default:
      return tipo;
  }
};

export const DocumentCard: React.FC<DocumentCardProps> = ({ documento }) => {
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.600');

  const IconComponent = getDocumentIcon(documento.tipo);
  const colorScheme = getDocumentColor(documento.tipo);

  const handleDownload = () => {
    if (documento.url) {
      // Abre URL diretamente (S3 presigned URL)
      window.open(documento.url, '_blank');
    }
  };

  const canDownload = !!documento.url;

  return (
    <Box
      bg={bgColor}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      p={5}
      mb={3}
      transition="all 0.2s"
      boxShadow="0 2px 8px rgba(0, 0, 0, 0.06)"
      _hover={{
        bg: hoverBg,
        transform: 'translateY(-3px)',
        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.12)',
        borderColor: `${colorScheme}.300`,
      }}
    >
      <VStack align="stretch" spacing={3}>
        {/* Header */}
        <HStack justify="space-between">
          <HStack spacing={2}>
            <Icon as={IconComponent} color={`${colorScheme}.500`} boxSize={5} />
            <Badge colorScheme={colorScheme} fontSize="xs">
              {getDocumentLabel(documento.tipo)}
            </Badge>
          </HStack>
        </HStack>

        {/* Título */}
        <Text fontWeight="bold" fontSize="md">
          {documento.titulo}
        </Text>

        {/* Descrição */}
        {documento.descricao && (
          <Text fontSize="sm" color="gray.600">
            {documento.descricao}
          </Text>
        )}

        {/* Ações */}
        <HStack spacing={2}>
          {canDownload ? (
            <Button
              leftIcon={<Download size={16} />}
              size="sm"
              colorScheme={colorScheme}
              onClick={handleDownload}
              borderRadius="lg"
              fontWeight="semibold"
              boxShadow="0 2px 6px rgba(0, 0, 0, 0.1)"
              _hover={{
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
              }}
            >
              Baixar
            </Button>
          ) : (
            <Text fontSize="xs" color="gray.500" fontStyle="italic">
              Download não disponível
            </Text>
          )}
        </HStack>
      </VStack>
    </Box>
  );
};
